import type { TableSorting } from "@aurem/ui";
import { useCallback, useMemo } from "react";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UseTableSortingProps<TFilters extends Record<string, any>> = {
  filters: TFilters;
  setFilters: (filters: Partial<TFilters>) => void;
  removeFilters: (keys: (keyof TFilters)[]) => void;
  sortFieldKey: keyof TFilters;
  sortOrderKey: keyof TFilters;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useTableSorting = <TFilters extends Record<string, any>>({
  filters,
  setFilters,
  removeFilters,
  sortFieldKey,
  sortOrderKey,
}: UseTableSortingProps<TFilters>): TableSorting => {
  const onSort = useCallback(
    (key: string, direction: "asc" | "desc" | null) => {
      if (direction === null) {
        removeFilters([sortFieldKey, sortOrderKey]);
      } else {
        setFilters({
          [sortFieldKey]: key,
          [sortOrderKey]: direction,
        } as Partial<TFilters>);
      }
    },
    [setFilters, removeFilters, sortFieldKey, sortOrderKey],
  );

  return useMemo((): TableSorting => {
    const field = filters[sortFieldKey];
    const order = filters[sortOrderKey];

    return {
      key: field || null,
      direction: (order as "asc" | "desc") || null,
      onSort,
      server: true,
    };
  }, [filters, sortFieldKey, sortOrderKey, onSort]);
};
